import { useMutation } from "@apollo/client/react";
import axios from "axios";
import { useState } from "react";

import { signS3Attachment } from "../api/SignS3";
import {
  TEMPLATE_FIELDS,
  TEMPLATE_TYPES,
} from "../components/autotemplates/constants";
import {
  cleanExtractedData,
  processCustomFields,
  processSoapFields,
} from "../components/autotemplates/TemplateBuilder/utils";
import { alert } from "../components/common/Alert";
import { UPLOAD_PDF_AND_EXTRACT_TEXT } from "../graphql/mutations/AutoTemplates";

/* eslint-disable @typescript-eslint/no-explicit-any */

interface ExtractTemplateFromPdfParams {
  file: File;
  templateType?: string;
  onExtract?: (fields: any) => void;
}

export function usePdfTemplateExtraction() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | string | null>(null);
  const [uploadPdfAndExtractText] = useMutation(
    UPLOAD_PDF_AND_EXTRACT_TEXT,
  );

  const extractTemplateFromPdf = async ({
    file,
    templateType = TEMPLATE_TYPES.CUSTOM,
    onExtract,
  }: ExtractTemplateFromPdfParams) => {
    setLoading(true);
    setError(null);
    if (!file || file.type !== "application/pdf") {
      alert("warning", "Please upload a valid PDF file.");
      setLoading(false);
      setError("Invalid file type");
      return;
    }
    try {
      const signS3Response = await signS3Attachment(
        "pdf",
        "templates" as any,
      );
      const { data, storageObjectName } = signS3Response.data;
      const { fields: s3Fields } = data;

      const fileData = new FormData();
      for (const key in s3Fields) {
        fileData.append(key, s3Fields[key]);
      }
      fileData.append("file", file);
      fileData.append("Content-Type", file.type);

      await axios.post(data.url, fileData, {
        headers: { "Access-Control-Allow-Origin": true },
      });

      const response = await uploadPdfAndExtractText({
        variables: {
          storageObjectName,
          templateType,
        },
      });
      const extracted = response.data?.uploadPdfAndExtractText;
      if (extracted) {
        const cleanedData = cleanExtractedData(extracted);
        let updatedFields;
        if (templateType === TEMPLATE_TYPES.SOAP) {
          updatedFields = processSoapFields(cleanedData);
        } else {
          updatedFields = processCustomFields(
            cleanedData[TEMPLATE_FIELDS.CUSTOM],
          );
        }
        if (onExtract) onExtract(updatedFields);
      }
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { extractTemplateFromPdf, loading, error };
}
